import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { setBranchUserId, logOutBranchUser } from "lib/branchLib";
import { setAnalyticsUserProperties } from "utility/FirebaseAnalytics";
import { trackEvent } from "utility/mixpanel";
import {
  bootstrapSession,
  clearAccessToken,
  completeSSO,
  fetchMe,
  getAccessToken,
  hasSessionHint,
  revokeSession,
} from "providers/auth-tokens";
import {
  EMAIL_DRAFT_KEY,
  JUST_LOGGED_OUT_KEY,
} from "providers/auth-storage-keys";
import { clearLastUsedEmail, setLastUsedEmail } from "utility/last-used-email";

import {
  LEARNER_BIOMETRIC_DISMISS_WEB_MODAL,
  LEARNER_UPDATE_LANGUAGE,
} from "../utility/graphQl/mutation";
import { QUERY_USER_PROFILES } from "../utility/graphQl/query";
import {
  DEFAULT_LOCALE,
  getBrowserLanguageTag,
  resolveSupportedLocale,
} from "utility/locale";
import {
  markLanguageChoiceSynced,
  readLanguageChoice,
  shouldSeedFromBrowser,
} from "utility/languagePreference";

import { useLazyQuery, useMutation, useApolloClient } from "@apollo/client";
import { setGlobalInfoCookie, clearStorage } from "../utility/Function";
import {
  logAnalyticsEvent,
  setAnalyticsUserId,
} from "../utility/FirebaseAnalytics";
import { setUserId as setMixpanelUserId } from "../utility/mixpanel";

import appInfo from "utility/app-info";
import { emitEvent } from "utility/events";

import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { logError } from "utility/Function";

const AuthContext = createContext(null);

export const WONE_ADMIN_ROLE = "ADMIN";
export const COMPANY_ADMIN_ROLE = "COMPANY-ADMIN";
export const STUDENT_ROLE = "STUDENT";

const updateLocalStorage = (user) => {
  _setCommonLocalStorage(user);

  if (checkUserHasRole(user, STUDENT_ROLE)) {
    _setStudentLocalStorage(user);
  }

  if (checkUserHasRole(user, COMPANY_ADMIN_ROLE)) {
    _setCompanyAdminLocalStorage(user);
  }
};

const _setCommonLocalStorage = (user) => {
  const { firstName, lastName, id, email, userdetail } = user;

  const companyName = userdetail?.studentdetail?.company?.name || "NA";

  // Remove student specific items
  localStorage.removeItem("studentID");
  localStorage.removeItem("studentPhoto");
  localStorage.removeItem("WOEstudentUserId");
  localStorage.removeItem("shouldOnboard");

  // Remove company admin specific items
  localStorage.removeItem("paymentLink");
  localStorage.removeItem("companyID");
  localStorage.removeItem("roleID");

  // Set common items
  localStorage.setItem("companyName", companyName);
  localStorage.setItem("userfirstName", firstName);
  localStorage.setItem("userlastName", lastName);
  localStorage.setItem("userID", id);
  localStorage.setItem("userEmailId", email);
};

const _setStudentLocalStorage = (user) => {
  const {
    id,
    userdetail: { studentdetail, photo },
  } = user;
  localStorage.setItem("studentID", studentdetail.id);
  localStorage.setItem("studentPhoto", photo);
  localStorage.setItem("WOEstudentUserId", id); // TODO: remove from codebase and refactor all dependants

  if (!studentdetail?.onboarding) {
    localStorage.setItem("shouldOnboard", "true");
  } else {
    localStorage.removeItem("shouldOnboard");
  }
};

const _setCompanyAdminLocalStorage = (user) => {
  const {
    id,
    userdetail: { studentdetail },
  } = user;
  //const paymentLink = studentdetail.company.paymentLink;

  /* if (paymentLink) {
    localStorage.setItem("paymentLink", paymentLink);
  } */
  localStorage.setItem("companyID", id);
  localStorage.setItem("studentID", studentdetail.id);
  //localStorage.setItem("roleID", role[0].id);
};

export const getCurrentUserRoleType = (roles) => {
  if (roles && roles.find((item) => item === STUDENT_ROLE)) {
    return STUDENT_ROLE;
  } else {
    return null;
  }
};

export const getUserRoleType = (user) => {
  if (
    user &&
    user.userdetail.role.find((item) => item.name === STUDENT_ROLE)
  ) {
    return STUDENT_ROLE;
  } else {
    return null;
  }
};

export const checkUserHasRole = (user, role) => {
  if (user?.userdetail?.role) {
    return user.userdetail.role.some((item) => item.name === role);
  } else {
    return undefined;
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const setSearchParams = useSearchParams()[1];

  const [authToken, setAuthToken] = useState("");
  const [user, setUser] = useState(null);
  // Tri-state outcome of the initial cookie-refresh probe
  // ("pending" | "ready" | "error"). The auth gate in user/index.js waits
  // for "pending" to clear before deciding what to render — a stale
  // ``localStorage["Authed"]`` from a revoked session, or a boot request
  // that hangs/fails, must never leave the gate stuck on a spinner
  // forever. "error" is recoverable via retryBootstrap().
  const [bootStatus, setBootStatus] = useState("pending");
  const mountedRef = useRef(true);
  const [userProfiles, setUserProfiles] = useState({
    learner: null,
    companyAdmin: null,
  });

  // WONE-565 (Phase 4b-1): legacy `signIn` mutation retired. Loading
  // and error state for the new POST /api/v1/auth/login flow lives in
  // local state instead, preserving the existing context API shape.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    Sentry.setUser({ id: user?.id });
  }, [user?.id]);

  const handleUserProfiles = (user, userProfiles) => {
    // No learner or company-admin profile means the account has neither
    // STUDENT nor COMPANY-ADMIN role — likely a legacy TEACHER-only
    // user post practitioner-portal deprecation. Surface a toast and
    // sign them out so the routing gate doesn't get stuck on `/`.
    if (!userProfiles?.learner && !userProfiles?.companyAdmin) {
      toast.error(
        "This account doesn't have access to the WONE app. Please contact support@walkingonearth.com."
      );
      endSession({ forced: true });
      return;
    }

    // State part
    setUser(user);
    setUserProfiles(userProfiles);
    setBranchUserId(user.id);
    setAnalyticsUserId(user.id);
    setMixpanelUserId(user.id);

    const { learner } = userProfiles;
    if (learner) {
      let has_health_data = "false";
      let has_connected_biometrics = "false";
      let biometrics_provider = "none";

      if (learner?.biometrics?.vitalUserId) {
        has_health_data = learner.biometrics.hasHealthData;

        if (learner.biometrics.providers.length > 0) {
          has_connected_biometrics = "true";
          biometrics_provider = learner.biometrics.providers[0].name;
        } else {
          has_connected_biometrics = "false";
          biometrics_provider = "none";
        }
      }

      setAnalyticsUserProperties({
        company_name: learner.companyName,
        has_onboarded: !learner?.onboarding ? "true" : "false",
        has_connected_biometrics,
        has_health_data,
        biometrics_provider,
      });
    }

    setGlobalInfoCookie({ id: user.id });

    // Local storage part
    updateLocalStorage(user);
  };

  const handleUser = (user) => {
    setUser(user);
    setAnalyticsUserId(user.id);
    setMixpanelUserId(user.id);
    setBranchUserId(user.id);
    setGlobalInfoCookie({ id: user.id });
    updateLocalStorage(user);
  };

  const [
    getUserProfiles,
    { refetch: refetchUserProfiles, loading: userProfilesLoading },
  ] = useLazyQuery(QUERY_USER_PROFILES, {
    variables: {
      appInfo,
      email: user?.email,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted(result) {
      const { user, userProfiles } = result;

      handleUserProfiles(user, userProfiles);
    },
    onError(error) {
      logError("Error fetching user profiles", error);
      endSession({ forced: true });
      navigate(0);
    },
  });

  // WONE-589: the legacy `login` and SSO token-redeem (`loginWithToken`)
  // were removed (/signin via the legacy Branch hand-off). Entry points
  // are ``completeSSOLogin`` for PKCE-based SSO, the OTP verify helpers
  // for email codes, and ``loginWithPassword`` (providers/auth-tokens.ts)
  // for flagged accounts — the latter two followed by
  // ``loadCurrentSession``.

  /**
   * Shared identity-bootstrap path. Calls /api/v1/auth/me with the
   * current bearer, fetches the GraphQL user profile, and primes the
   * local user/profile state. Returns the loaded user + profiles.
   *
   * ``rejectedMessage`` / ``unavailableMessage`` let the caller customise
   * the thrown error text — credential-issuing flows (post-OTP, post-SSO)
   * surface a different label than the cold-start cookie probe.
   */
  const hydrateSessionFromMe = async ({
    rejectedMessage,
    unavailableMessage,
  }) => {
    const me = await fetchMe();
    if (me.status !== "ok") {
      const rejected = me.status === "rejected";
      const err = new Error(rejected ? rejectedMessage : unavailableMessage);
      // Stable machine-readable cause so /auth/callback's Mixpanel
      // ``Auth SSO Error`` (and Sentry, WONE-740) report the real
      // failure instead of a catch-all (WONE-713 review).
      err.code = rejected ? "session_rejected" : "identity_unavailable";
      throw err;
    }
    let profileResult;
    try {
      profileResult = await client.query({
        query: QUERY_USER_PROFILES,
        variables: { appInfo, email: me.user.email },
        fetchPolicy: "network-only",
      });
    } catch (err) {
      err.code = err.code || "profile_load_failed";
      throw err;
    }
    const { user, userProfiles } = profileResult.data;
    setAuthToken(getAccessToken() || "");
    setLastUsedEmail(me.user.email);
    handleUserProfiles(user, userProfiles);
    return { user, userProfiles };
  };

  /**
   * WONE-569: load the user profile for the active session and set
   * ``user`` / ``userProfiles`` state. Used after token-issuing flows
   * that don't already do the load themselves — currently the
   * password-reset redemption (``change-password``) screen.
   *
   * Identity comes from ``GET /api/v1/auth/me`` keyed by the
   * in-memory bearer. Throws if the server rejects the bearer or the
   * call is unavailable — callers (e.g. ``change-password.tsx``) MUST
   * surface the failure rather than navigating to a wedge.
   */
  const loadCurrentSession = () =>
    hydrateSessionFromMe({
      rejectedMessage: "session rejected after credential issuance",
      unavailableMessage: "identity service unavailable",
    });

  /**
   * WONE-565 (Phase 4b-3): complete a PKCE-based SSO sign-in. Called
   * by /auth/callback after Platform's /o/authorize hands back a code.
   * The token exchange happens via completeSSO (auth-tokens.ts) which
   * also writes the access token into the in-memory store. We then
   * fetch the user profile via QUERY_USER_PROFILES and mirror the
   * legacy loginWithToken bookkeeping (analytics, role routing).
   */
  // Mixpanel for the SSO flow lives in /auth/callback's ``Auth SSO
  // Started/Completed/Error`` family (WONE-712 taxonomy, mobile parity)
  // — the legacy ``Attempting to Sign In`` / ``Successfully Signed In``
  // / ``Attempting to Sign In Failed`` triple was removed here so one
  // SSO sign-in isn't double-counted (WONE-713 review). Firebase
  // ``logAnalyticsEvent`` is a separate system and stays.
  const completeSSOLogin = async (code, state, channelLabel) => {
    setLoading(true);
    setError(null);
    try {
      const { returnTo } = await completeSSO(code, state);

      // WONE-569: identity comes from GET /api/v1/auth/me using the
      // freshly-issued bearer. No more localStorage fallback — the
      // cookie + access token is enough to identify the user. Both
      // failure modes mean we can't safely render the home screen, so
      // hydrateSessionFromMe surfaces it.
      const { user } = await hydrateSessionFromMe({
        rejectedMessage: "SSO session rejected by server",
        unavailableMessage: "identity service unavailable after SSO",
      });

      const roleType = getUserRoleType(user);

      const id = localStorage.getItem("userID");
      const companyName = localStorage.getItem("companyName");
      logAnalyticsEvent("login", {
        user_id: id,
        user_id_wone: id,
        company_name: companyName,
        channel: channelLabel,
      });

      navigate(returnTo || "/");
      return { ok: true, user, roleType };
    } catch (err) {
      setError(err);
      logAnalyticsEvent("login_failed", { channel: channelLabel });
      logError("SSO sign-in failed", err);
      Sentry.captureException(err, {
        tags: { flow: "sso_login", channel: channelLabel },
      });
      trackEvent("Attempting to Sign In Failed", {
        Method: channelLabel,
        Error: err,
      });
      // Surface the failure on the unified entry screen rather than
      // landing the user there silently. ``email-entry.tsx`` reads
      // ``?error=`` and renders it as an inline error.
      const reason = err?.message || "sso_failed";
      navigate(`/email-entry?error=${encodeURIComponent(reason)}`);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // WONE-589: ``loginWithToken`` (legacy SSO Branch deep-link redeem)
  // and the ``signOutCall`` no-op stub are gone. SSO now lands at
  // /auth/callback and runs through completeSSOLogin instead.

  // ``forced``: the app ended the session, not the learner. A forced
  // sign-out falls through to the auth gate's /email-entry bounce; a chosen
  // one (``logout``) restarts at the welcome screen like a fresh visit.
  const endSession = async ({ forced }) => {
    trackEvent("Attempting to Sign Out");
    // Revoke the OAuth session via the credential API. The endpoint
    // relies on the HttpOnly refresh cookie and tolerates a missing
    // bearer, so call it unconditionally — skipping it when the in-
    // memory token is empty (e.g. logout fired during bootstrap) would
    // leave __Secure-wone_refresh alive and the next page load would
    // silently re-authenticate the user.
    //
    // A failed revoke still clears everything local below, but it is not a
    // clean sign-out: inside the desktop shell it can mean the keychain kept
    // a refresh token that would sign this user back in on the next launch.
    // So it is rethrown at the end and "Signed Out" is not reported.
    let revokeError = null;
    try {
      await revokeSession();
    } catch (err) {
      revokeError = err;
    }
    clearStorage();
    setAuthToken(null);
    setUser(null);
    setUserProfiles({
      learner: null,
      companyAdmin: null,
    });
    setAnalyticsUserId(null);
    setAnalyticsUserProperties({
      company_name: null,
      has_onboarded: null,
      has_connected_biometrics: null,
      has_health_data: null,
      biometrics_provider: null,
    });
    logOutBranchUser();
    client.clearStore();
    if (revokeError === null) trackEvent("Signed Out");
    // Before navigating, so the next page view isn't sent as this user's.
    setMixpanelUserId(null);
    if (forced) {
      setSearchParams({});
      // One-shot flag: email-entry is the root of the auth stack here, so
      // it hides its back chevron.
      try {
        sessionStorage.setItem(JUST_LOGGED_OUT_KEY, "1");
      } catch (err) {
        logError("Failed to set the just-logged-out flag", err);
      }
    } else {
      // Same tick as the state clear above, so the auth gate's first
      // signed-out render already sees /get-started, which it lets through,
      // instead of bouncing the protected page to /email-entry. A push, not
      // a replace: replacing would leave the sign-in screen that led here
      // one Back away.
      navigate("/get-started");
    }
    // WONE-612: end-of-flow cleanup. The cached email + in-flight draft
    // shouldn't follow the user past logout — web is multi-user-likely
    // (vs mobile's single-keychain assumption), so the next /auth/email
    // visit starts blank.
    clearLastUsedEmail();
    try {
      sessionStorage.removeItem(EMAIL_DRAFT_KEY);
    } catch {}
    if (revokeError !== null) throw revokeError;
  };

  const logout = () => endSession({ forced: false });

  const [dismissBiometricsWebModal] = useMutation(
    LEARNER_BIOMETRIC_DISMISS_WEB_MODAL
  );

  const handleDismissBiometricsWebModal = () => {
    dismissBiometricsWebModal({
      variables: {
        appInfo,
      },
    }).then((response) => {
      const learner = response.data.learnerBiometricsDismissWebModal.learner;

      setUserProfiles({
        ...userProfiles,
        learner,
      });
    });
  };

  // Patch fields onto the in-memory learner. `learner` is provider state, not
  // reactively derived from the Apollo cache, so a mutation's cache write alone
  // won't flip context consumers — callers that mutate the learner must merge
  // the change back here.
  const updateLearner = (partial) => {
    setUserProfiles((prev) => ({
      ...prev,
      learner: prev.learner ? { ...prev.learner, ...partial } : prev.learner,
    }));
  };

  const [updateLanguage] = useMutation(LEARNER_UPDATE_LANGUAGE);

  // Reconcile the learner's stored language with what they've chosen in this
  // browser. Two cases, in priority order:
  //
  //   1. A choice made here that hasn't reached the profile yet — the picker
  //      is offered on the sign-in screen, before there is an account, so
  //      this is where that choice lands. It is deliberate and overwrites
  //      whatever the profile holds.
  //   2. Otherwise seed from the browser locale as a convenience, but only
  //      while the learner has never explicitly chosen.
  //
  // Both are best-effort: a failure leaves the profile as it was and the next
  // sign-in retries.
  const syncLanguagePreference = useCallback(
    async (learner) => {
      if (!learner) return;

      const stored = readLanguageChoice();
      if (stored?.pendingSync) {
        try {
          await updateLanguage({
            variables: {
              language: stored.locale,
              explicit: true,
              appInfo,
            },
          });
          updateLearner({
            language: stored.locale,
            languageIsExplicit: true,
          });
          // Only after the write lands: while it's pending the local choice
          // outranks the profile, and clearing the flag early would let a
          // stale profile language win on the next load.
          markLanguageChoiceSynced();
          // Server-composed content (onboarding form inputs, Index copy,
          // filters) already in flight or cached was requested under the
          // profile's previous language — refetch it in the chosen one.
          client.reFetchObservableQueries().catch(logError);
        } catch (error) {
          logError(error);
        }
        return;
      }

      if (!shouldSeedFromBrowser(learner)) return;
      const language = resolveSupportedLocale(getBrowserLanguageTag());
      if (!language || language === DEFAULT_LOCALE) return;

      try {
        // Deliberately not `explicit`: a browser seed is a guess, not a choice.
        await updateLanguage({ variables: { language, appInfo } });
        updateLearner({ language });
      } catch (error) {
        logError(error);
      }
    },
    [updateLanguage, client]
  );

  // Record a language the learner chose deliberately. Lives here rather than
  // in the picker so the picker carries no Apollo dependency — it is mounted
  // in the shared auth shell, which several screens render without a client.
  const chooseLanguage = useCallback(
    async (locale) => {
      await updateLanguage({
        variables: { language: locale, explicit: true, appInfo },
      });
      updateLearner({ language: locale, languageIsExplicit: true });
      markLanguageChoiceSynced();
      // Cached server-composed content is still in the previous language.
      client.reFetchObservableQueries().catch(logError);
    },
    [updateLanguage, client]
  );

  // WONE-589: legacy password-signup GraphQL surface removed.
  // ``learnerSignUpWithPassword`` + ``learnerRequestSignUpWithPassword``
  // were the back-end of the deleted create-account.tsx; the
  // ``learnerCreateAccountScreen`` lazy query was its pre-flight check.
  // Verify-via-OTP is the only surviving signup path
  // (``verifySignupOtp`` in providers/auth-tokens.ts).

  // Fire app.opened once per real page load (fire-and-forget; the 30-min
  // throttle lives inside emitEvent). Separate from the auth bootstrap so it
  // runs on every cold load regardless of auth outcome. StrictMode's dev
  // double-invoke is absorbed by the throttle.
  useEffect(() => {
    emitEvent("app.opened", { trigger: "cold_start" });
  }, []);

  // Fire user.identified once per (device, user) binding: on first login and
  // whenever a different user signs in on the same browser. Suppressed on
  // plain reloads while already logged in — dedup lives inside emitEvent and
  // is keyed on user id, not time. Both password/OTP and SSO paths converge
  // on setting `user`, so watching user?.id covers every entry point.
  useEffect(() => {
    if (user?.id) emitEvent("user.identified");
  }, [user?.id]);

  // Seed language from the browser locale once per learner, on first sign-in.
  // Keyed on the learner id so it fires after the profile lands (a separate
  // state tick from `user`) and re-evaluates when a different user signs in.
  useEffect(() => {
    if (userProfiles?.learner?.id) {
      void syncLanguagePreference(userProfiles.learner);
    }
    // Keyed on the learner id only — seed once per user, not on every profile
    // field change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfiles?.learner?.id, syncLanguagePreference]);

  // The actual boot probe, extracted so both the mount effect and a
  // user-triggered "try again" can drive the same tri-state transition.
  // Guarded by bootRunningRef so a retry click can't overlap an
  // in-flight probe (bootstrapSession() already single-flights the
  // underlying refresh call, but the identity fetch + profile load that
  // follow aren't otherwise safe to run twice concurrently).
  const bootRunningRef = useRef(false);

  const retryBootstrap = useCallback(async () => {
    if (bootRunningRef.current) return;
    bootRunningRef.current = true;
    setBootStatus("pending");
    try {
      // OAuth 2.1 session bootstrap: if __Secure-wone_refresh is present,
      // /api/v1/auth/refresh mints a fresh access token without user
      // interaction and /api/v1/auth/me identifies the user. The legacy
      // HS256 ``Authtoken`` localStorage path retired with WONE-589 / 552
      // — anyone landing here without an OAuth refresh cookie has to
      // sign in via the unified flow.
      //
      // A browser with no session hint has nothing to recover, so it gets
      // one refresh attempt rather than the full retry budget: most such
      // boots are email link scanners rendering invite and content links
      // from datacentre IPs that block the refresh call, and each one was
      // costing Platform three calls and sitting on the spinner for the
      // whole budget. One attempt still serves a cookie whose sentinel
      // went missing.
      const sessionHint = hasSessionHint();
      const oauthOutcome = await bootstrapSession(
        sessionHint ? undefined : { maxAttempts: 1 }
      );
      if (!mountedRef.current) return;

      if (oauthOutcome.kind === "terminal") {
        // Refresh cookie definitively rejected (401/403) — attemptRefresh
        // already cleared the Authed flag, so the gate resolves to
        // sign-in. Nothing to retry.
        setBootStatus("ready");
        return;
      }

      if (oauthOutcome.kind === "transient" && !sessionHint) {
        // Network/CORS/timeout with no session to recover: the failure
        // changes nothing for this browser, so the gate resolves to
        // sign-in as it would after a terminal outcome. Reporting these
        // buried the real ones under thousands of scanner events.
        setBootStatus("ready");
        return;
      }

      if (oauthOutcome.kind === "keep" || oauthOutcome.kind === "transient") {
        // ``transient`` — network/CORS/5xx/timeout, retries exhausted.
        // ``keep`` — a non-auth non-ok response (e.g. 404 during a
        // Platform partial rollout, or a misrouted CDN): the session
        // cookie + Authed flag are deliberately left intact, but no user
        // loaded. ``keep`` is a server-side problem worth seeing even from
        // a signed-out visitor. Both must surface the retryable error
        // screen rather than falling through to the ``authed && !user``
        // spinner, which nothing else re-drives.
        Sentry.captureException(
          new Error("Bootstrap: auth refresh unavailable"),
          {
            tags: {
              flow: "bootstrap",
              cause:
                oauthOutcome.kind === "keep"
                  ? "refresh_unexpected"
                  : "refresh_transient",
            },
          }
        );
        setBootStatus("error");
        return;
      }

      const oauthToken = oauthOutcome.token;
      // WONE-569: identity from GET /api/v1/auth/me. The three-state
      // result matters here: only ``rejected`` (401/403) clears the
      // bearer; ``unavailable`` (5xx, 404, network, timeout) leaves the
      // session intact so a Platform partial rollout or transient hiccup
      // doesn't sign every user out on next page load.
      const me = await fetchMe();
      if (!mountedRef.current) return;
      if (me.status === "ok") {
        setAuthToken(oauthToken);
        getUserProfiles({
          variables: { appInfo, email: me.user.email },
        });
        setBootStatus("ready");
      } else if (me.status === "rejected") {
        clearAccessToken();
        setBootStatus("ready");
      } else {
        // ``unavailable`` — keep the bearer in memory so retryBootstrap
        // (or the next refresh cycle) can recover without forcing the
        // user back through /signin.
        setAuthToken(oauthToken);
        Sentry.captureException(
          new Error("Bootstrap: identity fetch unavailable"),
          { tags: { flow: "bootstrap", cause: "me_unavailable" } }
        );
        setBootStatus("error");
      }
    } finally {
      bootRunningRef.current = false;
    }
  }, [getUserProfiles]);

  useEffect(() => {
    mountedRef.current = true;

    const userId = localStorage.getItem("userID");
    setBranchUserId(userId);
    setAnalyticsUserId(userId);
    setMixpanelUserId(userId);

    // We actively clear residual legacy keys so the ``Authed ||
    // Authtoken`` auth gate in user/index.js can't wedge on a stale flag
    // from a pre-OAuth session.
    try {
      localStorage.removeItem("Authtoken");
      localStorage.removeItem("refreshToken");
    } catch {}

    retryBootstrap();

    return () => {
      mountedRef.current = false;
    };
  }, [retryBootstrap]);

  return (
    // Using the provider so that ANY component in our application can
    // use the values that we are sending.
    <AuthContext.Provider
      value={{
        authToken,
        completeSSOLogin,
        loadCurrentSession,
        bootStatus,
        retryBootstrap,
        logout,
        loading,
        error,
        handleUser,
        setAuthToken,
        user,
        learner: userProfiles.learner,
        companyAdmin: userProfiles.companyAdmin,
        refetchUserProfiles,
        userProfilesLoading,
        onDismissBiometricsWebModal: handleDismissBiometricsWebModal,
        updateLearner,
        chooseLanguage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Auth hook
export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
